import { Link } from 'react-router-dom';
import { Shield } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ChatChannelResponse, ChatChannelMemberResponse } from '@/lib/api';
import { useArcheAvailability } from '@/hooks/useArcheAvailability';

interface ChannelHeaderProps {
  channel: ChatChannelResponse | null;
  memberCount?: number;
  currentUserId?: string | null;
  members?: ChatChannelMemberResponse[];
}

export function ChannelHeader({
  channel,
  memberCount,
  currentUserId,
  members,
}: ChannelHeaderProps) {
  const guildsAvailable = useArcheAvailability('arche_guilds');

  if (!channel) {
    return (
      <div className="h-14 border-b border-border bg-card px-4 flex items-center">
        <span className="text-sm text-muted-foreground">Select a channel</span>
      </div>
    );
  }

  // For DM channels, prefer dmPartnerDisplayName from the server
  let dmPartnerName: string | null = null;
  if (channel.channelType === 'direct') {
    dmPartnerName = channel.dmPartnerDisplayName ?? channel.dmPartnerId ?? null;
    if (!dmPartnerName && currentUserId && members && members.length > 0) {
      const partner = members.find((m) => m.userId !== currentUserId);
      if (partner) dmPartnerName = partner.userId;
    }
  }

  const displayName =
    channel.channelType === 'direct'
      ? dmPartnerName || channel.name || 'Direct Message'
      : channel.name || 'Unnamed Channel';

  return (
    <div className="h-14 border-b border-border bg-card px-4 flex items-center justify-between">
      <div className="flex items-center gap-3">
        <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
          <span className="text-sm font-medium text-primary">
            {channel.channelType === 'global'
              ? '#'
              : channel.channelType === 'direct'
                ? displayName.charAt(0).toUpperCase()
                : '#'}
          </span>
        </div>
        <div>
          <h2 className="text-sm font-semibold text-foreground">{displayName}</h2>
          {channel.description && (
            <p className="text-xs text-muted-foreground truncate max-w-xs">{channel.description}</p>
          )}
        </div>
      </div>
      <div className="flex items-center gap-4 text-xs text-muted-foreground">
        {channel.channelType === 'guild' &&
          channel.guildId &&
          (guildsAvailable ? (
            <Link to={`/a/arche_guilds/${channel.guildId}`}>
              <Button variant="ghost" size="sm">
                <Shield className="h-4 w-4 mr-1" />
                View Guild
              </Button>
            </Link>
          ) : (
            <Button variant="ghost" size="sm" disabled>
              <Shield className="h-4 w-4 mr-1" />
              View Guild
            </Button>
          ))}
        {memberCount !== undefined && (
          <span>
            {memberCount} member{memberCount !== 1 ? 's' : ''}
          </span>
        )}
      </div>
    </div>
  );
}
